import { useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import {
  ExclamationTriangleIcon,
  UsersIcon,
  IdentificationIcon,
  PhoneIcon,
  MapPinIcon,
  UserGroupIcon,
  HeartIcon,
} from "@heroicons/react/24/outline";
import { FormikSaveBar } from "../../../utils/FormikSaveBar";
import { PMField } from "../../../utils/PMField";
import { PMDialog } from "../../../utils/PMDialog";
import { useDispatch } from "react-redux";
import PMSwitch from "../../../utils/PMSwitch";
import { DepartmentSelector } from "../../../utils/moduleSelectors/departmentSelector";
import ModernSupplierSelector from "../../../utils/Selector/ModernSupplierSelector";
import UserChildrenTable from "./user-children-table";
import UserNextOfKinTable from "./user-next-of-kin-table";
import { dispatchWithToast } from "../../../Utils";
import { updateUserAsync } from "../../../../slices/user";
import { useCompanyHasModule } from "../../../../routes/roleOnly";
import { DocumentUploadSectionV5 } from "../../Media/DocumentUploadSectionV5";
import imgPlaceholder from "../../../../utils/img-placeholder.png";

export const UserInfo = (props) => {
  const { t } = useTranslation();
  const { item, onRefresh } = props;
  const dispatch = useDispatch();
  const websiteModuleActive = useCompanyHasModule("website");
  const isEmployee = !!item?.employment;
  const isEditable = item?.is_editable !== false; // Default to true if not set
  const [supplierSelectorOpen, setSupplierSelectorOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(
    item?.hired_supplier || null
  );

  const persistCollectionRows = useCallback(
    async (fieldName, rows) => {
      const res = await dispatch(
        updateUserAsync({
          id: item.id,
          [fieldName]: rows.map(({ _tempId, ...row }) => row),
        })
      );
      const data = res?.payload;
      if (!data || data.errors) {
        throw new Error(data?.errors?.[0] || t("user_profile.related_records.save_failed"));
      }
      if (onRefresh) onRefresh(data);
      return data[fieldName] || [];
    },
    [dispatch, item?.id, onRefresh, t]
  );

  const initialValues = useMemo(
    () => ({
      id: item?.id,
      name: item?.name,
      email: item?.email,
      private_email: item?.private_email,
      mobile: item?.mobile,
      mobile_work: item?.mobile_work,
      birth_date: item?.birth_date,
      postal_address: item?.postal_address?.id
        ? item?.postal_address
        : {
            address: item?.address,
            address2: item?.address_two,
            postal: item?.postal,
            place: item?.place,
          },
      physical_address: item?.physical_address?.id ? item?.physical_address : "",
      nextCustomerNumber: item?.nextCustomerNumber || false,
      user_children: (item?.user_children || []).map((row) => ({ ...row })),
      user_next_of_kins: (item?.user_next_of_kins || []).map((row) => ({ ...row })),
      submit: null,
      active: item?.active,
      show_in_dropdown: item?.show_in_dropdown,
      show_on_website: item?.show_on_website,
      image_url: item?.image_url || "",
      user_type_id: item?.user_type_id || 2,
      hired_employee_supplier_id: item?.hired_employee_supplier_id || null,
    }),
    [item]
  );

  return (
    <>
      {!isEditable && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 mt-0.5 mr-2" />
            <div className="flex-1">
              <p className="text-sm font-bold text-amber-900">
                {t("company.users.profile.read_only_title")}
              </p>
              <p className="text-sm text-amber-700 mt-1">
                {t("company.users.profile.read_only_multi_company")}
              </p>
            </div>
          </div>
        </div>
      )}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={Yup.object({
          name: Yup.string().max(255).required(t("customer.name.required")),
          description: Yup.string(),
        })}
        onSubmit={(values, helpers) => {
          try {
            const payload = {
              ...values,
              address: values?.postal_address?.address || "",
              postal: values?.postal_address?.postal || "",
              place: values?.postal_address?.place || "",
            };

            delete payload.physical_address;
            delete payload.short_description;

            dispatchWithToast(
              dispatch(updateUserAsync(payload)),
              (data) => {
                if (data && onRefresh) onRefresh(data);
              }
            );

            helpers.setStatus({ success: true });
            helpers.setSubmitting(false);
          } catch (err) {
            toast.error("Nope");
            console.error(err);
            helpers.setStatus({ success: false });
            helpers.setErrors({ submit: err.message });
            helpers.setSubmitting(false);
          }
        }}
      >
            {(formik) => {
              const isDirty = formik.dirty;

              return (
          <Form>
            <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(16,28,46,0.05)]">
              <div className="p-5">
                <div className="grid items-center gap-x-6 gap-y-4 sm:grid-cols-8">
                  <div className="sm:col-span-2">
                    <PMSwitch label="Active" name="active" {...formik} />
                  </div>
                  <div className="sm:col-span-2">
                    <PMSwitch
                      label="Show in dropdown"
                      name="show_in_dropdown"
                      {...formik}
                    />
                  </div>
                <div className="sm:col-span-2">
                  <PMSwitch
                    label="Show on website"
                    name="show_on_website"
                    disabled={!websiteModuleActive || !isEmployee}
                    {...formik}
                  />
                </div>
                {(!websiteModuleActive || !isEmployee) && (
                  <div className="sm:col-span-4 flex items-center">
                    <button
                      type="button"
                      className="text-sm font-semibold text-slate-500 underline hover:text-slate-700"
                      onClick={() => {
                        const reason = !websiteModuleActive
                          ? "Website module is not active for this company. Enable it under Company > Features to use this setting."
                          : "User must be an employee to appear on the website. Go to the Employment tab and create an employment record.";
                        window.alert(reason);
                      }}
                    >
                      Why is this disabled?
                    </button>
                  </div>
                )}
                </div>
              </div>
            </div>

            <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(16,28,46,0.05)]">
              <div className="flex items-center gap-2.5 border-b border-[#EEF1F5] px-5 py-3.5">
                <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[9px] bg-[#EAF3FD] text-[#1463C7]">
                  <UsersIcon className="h-4 w-4" />
                </span>
                <h3 className="text-sm font-bold text-slate-900">
                  {t("company.users.user_type.label")}
                </h3>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-6 gap-6">
                  <div className="col-span-6 sm:col-span-3">
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { id: 2, label: t("company.users.user_type.employee") },
                        { id: 3, label: t("company.users.user_type.hired") },
                      ].map((opt) => {
                        const active = formik.values.user_type_id === opt.id;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            disabled={!isEditable}
                            onClick={() => {
                              formik.setFieldValue("user_type_id", opt.id);
                              if (opt.id !== 3) {
                                formik.setFieldValue(
                                  "hired_employee_supplier_id",
                                  null
                                );
                                setSelectedSupplier(null);
                              }
                            }}
                            className={`rounded-[10px] border px-3 py-2 text-sm font-semibold transition-colors ${
                              active
                                ? "border-[#1463C7] bg-[#EAF3FD] text-[#0B3A78]"
                                : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                            } ${!isEditable ? "opacity-60 cursor-not-allowed" : ""}`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {formik.values.user_type_id === 3 && (
                    <div className="col-span-6 sm:col-span-3">
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                        {t("company.users.user_type.supplier")}
                      </label>
                      <button
                        type="button"
                        disabled={!isEditable}
                        onClick={() => setSupplierSelectorOpen(true)}
                        className="w-full flex items-center justify-between rounded-[10px] border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-slate-300 disabled:opacity-60"
                      >
                        <span className="truncate">
                          {selectedSupplier
                            ? selectedSupplier.displayTitle || selectedSupplier.name
                            : t("company.users.user_type.select_supplier")}
                        </span>
                      </button>
                      <PMDialog
                        open={supplierSelectorOpen}
                        onClose={() => setSupplierSelectorOpen(false)}
                        size="md"
                        dense
                        zIndex={2400}
                      >
                        <div className="h-[70vh]">
                          <ModernSupplierSelector
                            open={supplierSelectorOpen}
                            selectedSuppliers={
                              selectedSupplier ? [selectedSupplier] : []
                            }
                            onChange={(suppliers) => {
                              const s = suppliers?.[0] || null;
                              setSelectedSupplier(s);
                              formik.setFieldValue(
                                "hired_employee_supplier_id",
                                s ? s.id : null
                              );
                            }}
                            onClose={() => setSupplierSelectorOpen(false)}
                          />
                        </div>
                      </PMDialog>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(16,28,46,0.05)]">
              <div className="flex items-center gap-2.5 border-b border-[#EEF1F5] px-5 py-3.5">
                <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[9px] bg-[#EAF3FD] text-[#1463C7]">
                  <IdentificationIcon className="h-4 w-4" />
                </span>
                <h3 className="text-sm font-bold text-slate-900">
                  {t("Detaljer")}
                </h3>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-6 gap-6">
                  <div className="col-span-6 sm:col-span-3">
                    <PMField
                      name="birth_date"
                      type="date"
                      label="Birthdate"
                      {...formik}
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-3 lg:col-span-2"></div>
                </div>
              </div>
            </div>

            <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(16,28,46,0.05)]">
              <div className="flex items-center gap-2.5 border-b border-[#EEF1F5] px-5 py-3.5">
                <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[9px] bg-[#EAF3FD] text-[#1463C7]">
                  <PhoneIcon className="h-4 w-4" />
                </span>
                <h3 className="text-sm font-bold text-slate-900">
                  {t("Contact information")}
                </h3>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-6 gap-6 items-start">
                  <div className="col-span-6 sm:col-span-2">
                    <div className="flex items-center gap-2">
                      <img
                        src={formik.values.image_url || imgPlaceholder}
                        alt="Profile"
                        className="h-16 w-16 rounded-full object-cover ring-2 ring-[#BFDCF7]"
                      />
                      <DocumentUploadSectionV5
                        multiple={false}
                        module="user"
                        moduleParam={item?.id}
                        required={false}
                        buttonStyle="compact"
                        iconOnly
                        allowedFileTypes="image/png, image/jpeg, image/jpg, image/webp"
                        parentCallback={(uploadedFiles) => {
                          const first = Array.isArray(uploadedFiles) ? uploadedFiles[0] : null;
                          const f = first?.file;
                          const url =
                            (f && typeof f.url === "string" && f.url) ||
                            (f?.thumb && typeof f.thumb.url === "string" && f.thumb.url) ||
                            "";
                          if (url) {
                            formik.setFieldValue("image_url", url);
                            dispatchWithToast(dispatch(updateUserAsync({ id: formik.values.id, image_url: url })));
                          }
                        }}
                      />
                    </div>
                    {formik.values.image_url && (
                      <button
                        type="button"
                        className="mt-2 text-xs font-semibold text-[#DC2626] hover:underline"
                        onClick={() => formik.setFieldValue("image_url", "")}
                      >
                        Remove image
                      </button>
                    )}
                  </div>
                  <div className="col-span-6 sm:col-span-4">
                    <PMField name="name" type="text" label="Name" {...formik} />
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <PMField
                      name="email"
                      type="text"
                      label={t("user_profile.field.email")}
                      {...formik}
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <PMField
                      name="private_email"
                      type="email"
                      label={t("user_profile.field.private_email")}
                      {...formik}
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <PMField
                      name="mobile"
                      type="text"
                      label={t("user_profile.field.mobile")}
                      {...formik}
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <PMField
                      name="mobile_work"
                      type="text"
                      label={t("user_profile.field.mobile_work")}
                      {...formik}
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <DepartmentSelector
                      selected={item?.department}
                      {...formik}
                    />
                  </div>

                </div>
              </div>
            </div>

            <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(16,28,46,0.05)]">
              <div className="flex items-center gap-2.5 border-b border-[#EEF1F5] px-5 py-3.5">
                <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[9px] bg-[#EAF3FD] text-[#1463C7]">
                  <MapPinIcon className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <h3 className="text-sm font-bold text-slate-900">
                    {t("user_info.address")}
                  </h3>
                  <p className="text-xs text-slate-500">
                    {t("user_info.address_hint")}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-6 p-5">
                <div className="col-span-3 sm:col-span-3">
                  <PMField
                    name="postal_address.address"
                    type="text"
                    label="Address"
                    {...formik}
                  />
                </div>
                <div className="col-span-3 sm:col-span-3">
                  <PMField
                    name="postal_address.address2"
                    type="text"
                    label="Address 2"
                    {...formik}
                  />
                </div>
                <div className="col-span-1 sm:col-span-1">
                  <PMField
                    name="postal_address.postal"
                    type="text"
                    label="Postal"
                    placeName="postal_address.place"
                    {...formik}
                  />
                </div>
                <div className="col-span-2 sm:col-span-2">
                  <PMField
                    name="postal_address.place"
                    type="text"
                    label="Place"
                    {...formik}
                  />
                </div>
              </div>
            </div>

            <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(16,28,46,0.05)]">
              <div className="flex items-center gap-2.5 border-b border-[#EEF1F5] px-5 py-3.5">
                <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[9px] bg-[#EAF3FD] text-[#1463C7]">
                  <UserGroupIcon className="h-4 w-4" />
                </span>
                <h3 className="text-sm font-bold text-slate-900">
                  {t("Barn")}
                </h3>
              </div>
              <div className="p-5">
                <UserChildrenTable
                  name="user_children"
                  syncKey={item?.version}
                  disabled={!isEditable}
                  onPersistRows={persistCollectionRows}
                  {...formik}
                />
              </div>
            </div>

            <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(16,28,46,0.05)]">
              <div className="flex items-center gap-2.5 border-b border-[#EEF1F5] px-5 py-3.5">
                <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[9px] bg-[#EAF3FD] text-[#1463C7]">
                  <HeartIcon className="h-4 w-4" />
                </span>
                <h3 className="text-sm font-bold text-slate-900">
                  {t("Nærmeste")}
                </h3>
              </div>
              <div className="p-5">
                <UserNextOfKinTable
                  name="user_next_of_kins"
                  syncKey={item?.version}
                  disabled={!isEditable}
                  onPersistRows={persistCollectionRows}
                  {...formik}
                />
              </div>
            </div>

            {/* <PromptIfDirty {...formik}/> */}
            <FormikSaveBar {...formik} isDirty={isDirty} disabled={!isEditable} />
          </Form>
              );
            }}
      </Formik>
    </>
  );
};
